import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const RecruitmentModule = () => {
  const [activeSection, setActiveSection] = useState("job-requisition");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handleRecruit = () => navigate('/access-recruit');

  const sectionContent = {
    "job-requisition": {
      title: "Create & Approve Requisitions",
      description: "Streamline the hiring process with our comprehensive job requisition management system. Create role requests with pre-approved JD templates that ensure consistency while allowing customization for specific requirements. Configure multi-level approval workflows with automated notifications to hiring managers and stakeholders. Set hiring budgets, define compensation ranges, and prioritize urgent roles with fast-track approval processes while maintaining complete audit trails and compliance documentation."
    },
    "sourcing-outreach": {
      title: "Candidate Sourcing",
      description: "Expand your talent reach with our multi-channel sourcing platform that posts roles to 50+ job boards and professional networks simultaneously. Manage employee referral programs with automated bonus tracking and real-time status updates. Utilize advanced Boolean search tools and sourcing filters to discover qualified candidates from diverse talent pools. Automate personalized outreach sequences with follow-up reminders and track pipeline metrics including source effectiveness and application conversion rates for optimized hiring strategies."
    },
    "applicant-tracking": {
      title: "Applicant Tracking System",
      description: "Efficiently manage your entire candidate pipeline with our AI-powered applicant tracking system. Automatically parse resumes to extract key information and create unified candidate profiles. Configure custom pipeline stages with drag-and-drop interface to match your organization's specific hiring workflow. Score candidates based on predefined criteria, categorize applicants with customizable tags, and execute bulk actions for mass communications. Maintain comprehensive compliance logs and store all candidate interactions in centralized, searchable profiles."
    },
    "interview-scheduling": {
      title: "Interview Scheduling & Panel",
      description: "Simplify interview coordination with our automated scheduling platform that syncs with Google/Outlook calendars to find mutual availability. Configure multi-panel interview rounds with automated assignment of panel members and coordination tools. Utilize structured interview kits with pre-defined questions and competency-based assessment frameworks. Collect feedback through secure submission forms with anonymized scoring to reduce unconscious bias. Generate detailed interview reports with consolidated feedback and track candidate performance across different rounds using comprehensive dashboards."
    },
    "offer-onboarding": {
      title: "Offer Management",
      description: "Seamlessly transition candidates to employees with our integrated offer and onboarding management system. Generate professional offer letters using customizable templates with electronic signature integration for quick acceptance. Manage comprehensive compensation packages with detailed breakdowns and multi-level approval workflows. Integrate background verification services with real-time status tracking and automate new-joiner checklists with digital document submission. Assign onboarding tasks automatically, match new hires with designated buddies, and provide self-service portals for a smooth transition experience."
    }
  };

  const sections = [
    { id: "job-requisition", label: "REQUISITIONS" },
    { id: "sourcing-outreach", label: "SOURCING" },
    { id: "applicant-tracking", label: "APPLICANTS" },
    { id: "interview-scheduling", label: "INTERVIEWS" },
    { id: "offer-onboarding", label: "OFFER & ONBOARD" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. Job Requisition */}
        <section
          ref={(el) => (sectionsRef.current["job-requisition"] = el)}
          data-section="job-requisition"
          className={`resto-section ${visibleSections.has("job-requisition") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Job Requisition</div>
            <h2 className="resto-section-title">
              {sectionContent["job-requisition"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["job-requisition"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleRecruit}>New Requisition</button>
              <button className="resto-secondary-btn" onClick={handleRecruit}>View Requests</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="https://res.cloudinary.com/www-hibob-com/fl_lossy,f_webp,q_auto/wp-website/uploads/Automated-workflows-1.png" className="resto-real-image" alt="Requisition" />
          </div>
        </section>

        {/* 2. Sourcing & Outreach */}
        <section
          ref={(el) => (sectionsRef.current["sourcing-outreach"] = el)}
          data-section="sourcing-outreach"
          className={`resto-section resto-reverse ${visibleSections.has("sourcing-outreach") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Sourcing</div>
            <h2 className="resto-section-title">
              {sectionContent["sourcing-outreach"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["sourcing-outreach"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleRecruit}>Post Job</button>
              <button className="resto-secondary-btn" onClick={handleRecruit}>Start Outreach</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="https://res.cloudinary.com/www-hibob-com/fl_lossy,f_webp,q_auto/wp-website/uploads/eSign-1.png" className="resto-real-image" alt="Sourcing" />
          </div>
        </section>

        <CTA/>

        {/* 3. Applicant Tracking */}
        <section
          ref={(el) => (sectionsRef.current["applicant-tracking"] = el)}
          data-section="applicant-tracking"
          className={`resto-section ${visibleSections.has("applicant-tracking") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Applicants</div>
            <h2 className="resto-section-title">
              {sectionContent["applicant-tracking"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["applicant-tracking"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleRecruit}>Open ATS</button>
              <button className="resto-secondary-btn" onClick={handleRecruit}>Bulk Actions</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/ret3.png" className="resto-real-image" alt="Applicants" />
          </div>
        </section>

        {/* 4. Interview Scheduling */}
        <section
          ref={(el) => (sectionsRef.current["interview-scheduling"] = el)}
          data-section="interview-scheduling"
          className={`resto-section resto-reverse ${visibleSections.has("interview-scheduling") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Interviews</div>
            <h2 className="resto-section-title">
              {sectionContent["interview-scheduling"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["interview-scheduling"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleRecruit}>Schedule Interview</button>
              <button className="resto-secondary-btn" onClick={handleRecruit}>View Panels</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/ret4.png" className="resto-real-image" alt="Interviews" />
          </div>
        </section>

        {/* 5. Offer & Onboarding */}
        <section
          ref={(el) => (sectionsRef.current["offer-onboarding"] = el)}
          data-section="offer-onboarding"
          className={`resto-section ${visibleSections.has("offer-onboarding") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Offers & Onboarding</div>
            <h2 className="resto-section-title">
              {sectionContent["offer-onboarding"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["offer-onboarding"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleRecruit}>Create Offer</button>
              <button className="resto-secondary-btn" onClick={handleRecruit}>Start Onboarding</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/ret5.png" className="resto-real-image" alt="Offer Onboarding" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default RecruitmentModule;